// ReconDashboard.jsx - Redesigned with tabs and collapsible task boxes
import React, { useState, useEffect, useCallback, Component } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { RO_STATUSES } from './constants';
import { StatusBadge, LoadingSpinner } from './shared';
import InspectionForm from './InspectionForm';
import RODetailPage from './RODetailPage';
import VendorNeedsBoard from './VendorNeedsBoard';
import PrecheckModal from './PrecheckModal';
import PartsRoom from './PartsRoom';
import { VehicleFilesButton } from '../VehicleFilesModal';
import VinDisplay from '../shared/VinDisplay';

// Error Boundary for RODetailPage
class ROErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('RODetailPage Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center">
          <div className="text-red-600 mb-4 text-lg font-semibold">Error loading repair order</div>
          <p className="text-neutral-500 mb-4 text-sm">{this.state.error?.message || 'Unknown error'}</p>
          <button 
            onClick={this.props.onBack} 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Collapsible Task Box Component
const TaskBox = ({ title, count, color, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  if (count === 0) return null;
  
  const colorStyles = {
    red: { bg: 'bg-red-50', border: 'border-red-200', header: 'bg-red-100', text: 'text-red-800', badge: 'bg-red-500' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', header: 'bg-orange-100', text: 'text-orange-800', badge: 'bg-orange-500' },
    yellow: { bg: 'bg-amber-50', border: 'border-amber-200', header: 'bg-amber-100', text: 'text-amber-800', badge: 'bg-amber-500' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', header: 'bg-blue-100', text: 'text-blue-800', badge: 'bg-blue-500' },
    gray: { bg: 'bg-neutral-50', border: 'border-neutral-200', header: 'bg-neutral-100', text: 'text-neutral-700', badge: 'bg-neutral-500' },
  };
  
  const style = colorStyles[color] || colorStyles.blue;
  
  return (
    <div className={`${style.bg} border ${style.border} rounded-lg overflow-hidden`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 ${style.header} flex items-center justify-between hover:opacity-90 transition`}
      >
        <span className={`font-semibold ${style.text}`}>{title}</span>
        <div className="flex items-center gap-3">
          <span className={`${style.badge} text-white text-sm font-bold px-2.5 py-0.5 rounded-full`}>
            {count}
          </span>
          <span className={`text-neutral-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </div>
      </button>
      {isOpen && (
        <div className="p-3 bg-white border-t border-neutral-100">
          {children}
        </div>
      )}
    </div>
  );
};

// Stat Card Component - now clickable
const StatCard = ({ label, value, subtext, onClick, active }) => (
  <button 
    onClick={onClick}
    className={`bg-white border rounded-lg px-4 py-3 text-left transition hover:border-blue-300 hover:bg-blue-50 ${
      active ? 'border-blue-500 ring-2 ring-blue-200' : 'border-neutral-200'
    }`}
  >
    <div className="text-2xl font-bold text-neutral-800">{value}</div>
    <div className="text-sm text-neutral-600">{label}</div>
    {subtext && <div className="text-xs text-neutral-400">{subtext}</div>}
  </button>
);

const ReconDashboard = ({ vehicles: parentVehicles, onRefresh }) => {
  const { currentUser, canSeeFinancials } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const showCosts = isAdmin || canSeeFinancials;

  // Tab state
  const [activeTab, setActiveTab] = useState('today');
  const [todaySubTab, setTodaySubTab] = useState('arb'); // Sub-tabs within Today: arb, precheck, inspections, parts, snoozed

  // View state
  const [view, setView] = useState('dashboard');
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [selectedROId, setSelectedROId] = useState(null);
  const [precheckVehicle, setPrecheckVehicle] = useState(null);

  // Data state
  const [needsInspection, setNeedsInspection] = useState([]);
  const [dashboardVehicles, setDashboardVehicles] = useState([]);
  const [vendorNeeds, setVendorNeeds] = useState([]);
  const [partsWaitingDelivery, setPartsWaitingDelivery] = useState([]);
  const [users, setUsers] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [arbAlerts, setArbAlerts] = useState([]);
  const [shopVehicles, setShopVehicles] = useState([]);
  const [allInventoryVehicles, setAllInventoryVehicles] = useState([]);
  const [vehiclesWithFiles, setVehiclesWithFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [roFilter, setRoFilter] = useState('active');
  
  // Stat card modal state
  const [statModal, setStatModal] = useState({ show: false, type: null });
  
  // Clock-in and workflow state
  const [clockStatus, setClockStatus] = useState({ clockedIn: false, entry: null });
  const [myTasks, setMyTasks] = useState([]);
  const [partsNeedingAttention, setPartsNeedingAttention] = useState(0);
  
  // Modal states
  const [snoozeModal, setSnoozeModal] = useState({ show: false });
  const [snoozeDays, setSnoozeDays] = useState(3);
  const [snoozeReason, setSnoozeReason] = useState('');
  const [adminModal, setAdminModal] = useState({ show: false });
  const [adminExplanation, setAdminExplanation] = useState('');
  
  // New RO form state
  const [showNewROForm, setShowNewROForm] = useState(false);
  const [newROType, setNewROType] = useState('inventory');
  const [selectedVehicleForRO, setSelectedVehicleForRO] = useState(null);
  const [customerForm, setCustomerForm] = useState({ customerName: '', customerPhone: '', year: '', make: '', model: '', trim: '', vin: '', mileage: '', color: '', customerComplaint: '' });
  const [customerDecoding, setCustomerDecoding] = useState(false);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);

  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // VIN decoder for customer vehicles
  const decodeCustomerVin = async (vin) => {
    if (!vin || vin.length !== 17) return;
    setCustomerDecoding(true);
    try {
      const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${vin}?format=json`);
      const data = await response.json();
      if (data.Results) {
        const get = (name) => data.Results.find(r => r.Variable === name)?.Value || '';
        setCustomerForm(prev => ({
          ...prev,
          year: get('Model Year') || prev.year,
          make: get('Make') || prev.make,
          model: get('Model') || prev.model,
          trim: get('Trim') || prev.trim
        }));
      }
    } catch (err) {
      console.error('VIN decode error:', err);
    } finally {
      setCustomerDecoding(false);
    }
  };

  // Load all dashboard data
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    
    try {
      const results = await Promise.allSettled([
        api.getVehiclesNeedingInspection(),
        api.getReconDashboardVehicles(),
        api.getVendorNeeds(),
        api.getPartsWaitingDelivery(),
        api.getUsers(),
        api.getVendors(),
        api.getArbAlerts(),
        api.getShopVehicles(),
        api.getVehicles()
      ]);
      
      setNeedsInspection(results[0].status === 'fulfilled' ? results[0].value : []);
      setDashboardVehicles(results[1].status === 'fulfilled' ? results[1].value : []);
      setVendorNeeds(results[2].status === 'fulfilled' ? results[2].value : []);
      setPartsWaitingDelivery(results[3].status === 'fulfilled' ? results[3].value : []);
      setUsers(results[4].status === 'fulfilled' ? results[4].value : []);
      setAllVendors(results[5].status === 'fulfilled' ? results[5].value : []);
      setArbAlerts(results[6].status === 'fulfilled' ? results[6].value : []);
      setShopVehicles(results[7].status === 'fulfilled' ? results[7].value : []);
      const inventoryData = results[8].status === 'fulfilled' ? results[8].value : [];
      setAllInventoryVehicles(inventoryData.filter(v => v.vehicleType !== 'shop_vehicle' && v.vehicleType !== 'customer_vehicle'));
      
      setInitialLoadDone(true);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setLoadError('Failed to load dashboard: ' + err.message);
      setInitialLoadDone(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load vehicles with files for Files tab
  const loadVehiclesWithFiles = useCallback(async () => {
    setFilesLoading(true);
    try {
      const data = await api.getVehiclesWithFiles();
      setVehiclesWithFiles(data || []);
    } catch (err) {
      console.error('Failed to load vehicles with files:', err);
    } finally {
      setFilesLoading(false);
    }
  }, []);

  // Load clock status and tasks on mount
  useEffect(() => {
    const loadClockAndTasks = async () => {
      try {
        const [clockData, tasksData] = await Promise.allSettled([
          api.getClockStatus(),
          api.getMyTasks()
        ]);
        if (clockData.status === 'fulfilled') {
          setClockStatus(clockData.value || { clockedIn: false, entry: null });
        }
        if (tasksData.status === 'fulfilled') {
          const pending = (tasksData.value || []).filter(t => t.status !== 'complete');
          setMyTasks(pending);
        }
      } catch (err) {
        console.error('Failed to load clock/tasks:', err);
      }
    };
    loadClockAndTasks();
  }, []);

  // Load data on mount
  useEffect(() => {
    loadDashboard();
    
    // Check for pending navigation
    const pendingVehicleId = localStorage.getItem('pendingReconVehicle');
    if (pendingVehicleId) {
      localStorage.removeItem('pendingReconVehicle');
      setSelectedVehicleId(parseInt(pendingVehicleId));
      setView('inspection');
    }
    
    const pendingInspectionId = localStorage.getItem('pendingInspectionVehicle');
    if (pendingInspectionId) {
      localStorage.removeItem('pendingInspectionVehicle');
      setSelectedVehicleId(parseInt(pendingInspectionId));
      setView('inspection');
    }
    
    const pendingCreateROId = localStorage.getItem('pendingCreateRO');
    if (pendingCreateROId) {
      localStorage.removeItem('pendingCreateRO');
      setSelectedVehicleId(parseInt(pendingCreateROId));
      setView('ro');
    }
    
    // Handle navigation to specific RO by ID
    const pendingROId = localStorage.getItem('pendingROId');
    if (pendingROId) {
      localStorage.removeItem('pendingROId');
      setSelectedROId(parseInt(pendingROId));
      setView('ro-detail');
    }
  }, [loadDashboard]);

  // Handle ARB action
  const handleArbAction = async (vehicleId, action, notes, arbType) => {
    try {
      await api.updateArbAction(vehicleId, action, notes, arbType);
      setArbAlerts(prev => prev.filter(v => v.id !== vehicleId));
      loadDashboard();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to update ARB status:', error);
      alert('Failed to update ARB status: ' + (error.message || 'Unknown error'));
    }
  };

  // Fetch service history
  const fetchServiceHistory = async (vin, customerName, customerPhone) => {
    setLoadingHistory(true);
    setServiceHistory([]);
    try {
      const params = new URLSearchParams();
      if (vin) params.append('vin', vin);
      if (customerName) params.append('customerName', customerName);
      if (customerPhone) params.append('customerPhone', customerPhone);
      const history = await api.request(`/repair-orders/service-history?${params.toString()}`);
      setServiceHistory(history || []);
    } catch (err) {
      console.error('Failed to fetch service history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle vehicle selection for new RO
  const handleSelectVehicleForRO = (vehicle) => {
    setSelectedVehicleForRO(vehicle);
    if (vehicle?.vin || vehicle?.customerName || vehicle?.customerPhone) {
      fetchServiceHistory(vehicle.vin, vehicle.customerName, vehicle.customerPhone);
    }
  };

  // Handle create RO from selected vehicle
  const handleCreateROFromSelection = () => {
    if (!selectedVehicleForRO) return;
    setShowNewROForm(false);
    setSelectedVehicleId(selectedVehicleForRO.id);
    setView('inspection');
    setSelectedVehicleForRO(null);
    setServiceHistory([]);
  };

  // Handle add outside service vehicle
  const handleAddOutsideService = async (e) => {
    e.preventDefault();
    try {
      const newVehicle = await api.createCustomerVehicle(customerForm);
      setShowNewROForm(false);
      setCustomerForm({ customerName: '', customerPhone: '', year: '', make: '', model: '', trim: '', vin: '', mileage: '', color: '', customerComplaint: '' });
      setSelectedVehicleId(newVehicle.id);
      setView('inspection');
      setServiceHistory([]);
      loadDashboard();
    } catch (err) {
      alert('Failed to create vehicle: ' + err.message);
    }
  };

  // Handle vehicle click
  const handleVehicleClick = (vehicle) => {
    if (vehicle.roId) {
      setSelectedROId(vehicle.roId);
      setView('ro-detail');
    } else {
      setSelectedVehicleId(vehicle.id);
      setView('inspection');
    }
  };

  // Handle back to dashboard
  const handleBack = () => {
    setView('dashboard');
    setSelectedVehicleId(null);
    setSelectedROId(null);
    loadDashboard();
    if (onRefresh) onRefresh();
  };

  // Handle RO created from inspection
  const handleROCreated = (roId) => {
    if (roId) {
      setSelectedROId(roId);
      setView('ro-detail');
      loadDashboard();
    } else {
      handleBack();
    }
  };

  // Handle mark complete
  const handleMarkComplete = async (e, vehicle) => {
    e.stopPropagation();
    if (!vehicle.roId) return;
    if (!window.confirm(`Mark ${vehicle.stockNumber} as complete?`)) return;
    
    try {
      await api.updateRepairOrder(vehicle.roId, { status: 'complete' });
      loadDashboard();
      if (onRefresh) onRefresh();
    } catch (err) {
      alert('Failed to mark complete: ' + err.message);
    }
  };

  // Handle delete RO
  const handleDeleteRO = async (roId, roNumber, stockNumber) => {
    if (!window.confirm(`Delete RO #${roNumber} for ${stockNumber}? This cannot be undone.`)) return;
    try {
      await api.deleteRepairOrder(roId);
      loadDashboard();
      if (onRefresh) onRefresh();
    } catch (error) {
      alert('Failed to delete RO: ' + (error.message || 'Unknown error'));
    }
  };

  // Handle snooze
  const handleSnooze = async () => {
    if (!snoozeModal.roId) return;
    try {
      await api.snoozeRepairOrder(snoozeModal.roId, snoozeDays, snoozeReason);
      setSnoozeModal({ show: false });
      setSnoozeDays(3);
      setSnoozeReason('');
      loadDashboard();
    } catch (error) {
      alert('Failed to snooze: ' + (error.message || 'Unknown error'));
    }
  };

  // Handle precheck snooze
  const handlePrecheckSnooze = async () => {
    try {
      const snoozeUntil = new Date();
      snoozeUntil.setDate(snoozeUntil.getDate() + snoozeDays);
      
      await api.updateVehicle(snoozeModal.vehicleId, { 
        precheckSnoozedUntil: snoozeUntil.toISOString(),
        precheckSnoozeReason: snoozeReason || null
      });
      
      setSnoozeModal({ show: false });
      setSnoozeDays(3);
      setSnoozeReason('');
      loadDashboard();
      if (onRefresh) onRefresh();
    } catch (err) {
      alert('Failed to snooze: ' + err.message);
    }
  };

  // Handle pre-check complete
  const handlePreCheckComplete = async (vehicle) => {
    if (!window.confirm(`Mark pre-check complete for ${vehicle.stockNumber}?`)) return;
    try {
      await api.updateVehicle(vehicle.id, { damageFlagged: false, needsFurtherReview: false });
      if (vehicle.roId) {
        await api.updateRepairOrder(vehicle.roId, { status: 'pending_delivery' });
      }
      loadDashboard();
      if (onRefresh) onRefresh();
    } catch (err) {
      alert('Failed to mark pre-check complete: ' + err.message);
    }
  };

  // Handle vendor vehicle click
  const handleVendorVehicleClick = (vehicle) => {
    const dashVehicle = dashboardVehicles.find(v => v.stockNumber === vehicle.stockNumber);
    if (dashVehicle?.roId) {
      setSelectedROId(dashVehicle.roId);
      setView('ro-detail');
    }
  };

  // Handle creating RO from precheck
  const handleCreateROFromPrecheck = (vehicle, precheckParts) => {
    setSelectedVehicleId(vehicle.id);
    setView('inspection');
  };

  // ============ VIEW ROUTING ============
  if (view === 'inspection' && selectedVehicleId) {
    return (
      <InspectionForm
        vehicleId={selectedVehicleId}
        users={users}
        onBack={handleBack}
        onROCreated={handleROCreated}
        showCosts={showCosts}
      />
    );
  }

  if (view === 'ro-detail' && selectedROId) {
    return (
      <ROErrorBoundary onBack={handleBack}>
        <RODetailPage
          roId={selectedROId}
          users={users}
          vendors={allVendors}
          onClose={handleBack}
          showCosts={showCosts}
        />
      </ROErrorBoundary>
    );
  }

  if (view === 'parts-room') {
    return (
      <div>
        <div className="flex items-center gap-4 mb-4">
          <button onClick={handleBack} className="px-4 py-2 bg-neutral-200 rounded-lg hover:bg-neutral-300">
            Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold">Parts Room</h1>
        </div>
        <PartsRoom onRefresh={onRefresh} />
      </div>
    );
  }

  // ============ COMPUTE DATA FOR DISPLAY ============
  
  // Pre-check vehicles (flagged, not delivered)
  const preCheckVehicles = needsInspection.filter(v => {
    if (!v.damageFlagged) return false;
    if (v.transportStatus === 'delivered') return false;
    if (v.precheckSnoozedUntilDelivered) return false;
    if (v.precheckSnoozedUntil && new Date(v.precheckSnoozedUntil) > new Date()) return false;
    return true;
  });
  
  // Inspection vehicles (delivered, need inspection)
  // Exclude vehicles with completed/closed ROs - they're done!
  const inspectionVehicles = needsInspection.filter(v => {
    if (v.transportStatus !== 'delivered') return false;
    if (v.precheckSnoozedUntil && new Date(v.precheckSnoozedUntil) > new Date()) return false;
    // If vehicle has an RO that is complete or closed, it's done - don't show
    if (v.roId && (v.roStatus === 'complete' || v.roStatus === 'closed')) return false;
    // If vehicle has a completed inspection and no open RO, it's done
    if (v.inspectionCompletedAt && !v.roId) return false;
    return v.roId || v.damageFlagged || v.needsFurtherReview;
  });
  
  // Active ROs (not complete/closed, not snoozed)
  const isROSnoozedOrWaiting = (v) => {
    const isSnoozed = v.snoozedUntil && new Date(v.snoozedUntil) > new Date();
    return isSnoozed;
  };
  
  const activeROs = dashboardVehicles.filter(v => 
    v.roStatus && 
    v.roStatus !== 'complete' && 
    v.roStatus !== 'closed' && 
    !isROSnoozedOrWaiting(v)
  );
  
  const snoozedROs = dashboardVehicles.filter(isROSnoozedOrWaiting);
  const completedROs = dashboardVehicles.filter(v => v.roStatus === 'complete' || v.roStatus === 'closed');
  
  // Stats - match Transport module logic
  // Inbound = Pending + Scheduled + In Transit (not delivered, not sold, has transport status)
  const inboundVehicles = allInventoryVehicles.filter(v => 
    v.status !== 'sold' && 
    v.status !== 'archived' &&
    v.transportStatus && 
    ['pending', 'scheduled', 'in_transit'].includes(v.transportStatus)
  );
  const inboundCount = inboundVehicles.length;
  
  // Delivered vehicles (all with transportStatus = delivered, not sold)
  // Note: deliveredAt timestamp may not be set, so we count all delivered vehicles
  const deliveredVehicles = allInventoryVehicles.filter(v => {
    if (v.transportStatus !== 'delivered') return false;
    if (v.status === 'sold' || v.status === 'archived') return false;
    return true;
  });
  const deliveredCount = deliveredVehicles.length;

  // Filter ROs based on current filter
  const getFilteredROs = () => {
    let ros = [];
    if (roFilter === 'active') ros = activeROs;
    else if (roFilter === 'snoozed') ros = snoozedROs;
    else if (roFilter === 'completed') ros = completedROs;
    else ros = dashboardVehicles;
    
    if (!searchTerm) return ros;
    const search = searchTerm.toLowerCase();
    return ros.filter(v => 
      (v.stockNumber || '').toLowerCase().includes(search) ||
      (v.make || '').toLowerCase().includes(search) ||
      (v.model || '').toLowerCase().includes(search) ||
      (v.roNumber || '').toLowerCase().includes(search)
    );
  };

  const filteredROs = getFilteredROs();

  // Loading state
  if (loading && !initialLoadDone) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <LoadingSpinner />
          <div className="text-neutral-500 mt-4">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ============ NEW RO BUTTON - TOP ============ */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold text-neutral-800">Recon Dashboard</h1>
        <button
          onClick={() => { setShowNewROForm(true); setNewROType('inventory'); }}
          className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 flex items-center gap-2"
        >
          <span className="text-lg">+</span> New RO
        </button>
      </div>

      {/* ============ STATS BAR ============ */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard 
          label="Inbound" 
          value={inboundCount} 
          subtext="vehicles in transit" 
          onClick={() => setStatModal({ show: true, type: 'inbound' })}
          active={statModal.type === 'inbound'}
        />
        <StatCard 
          label="Delivered" 
          value={deliveredCount} 
          subtext="on lot" 
          onClick={() => setStatModal({ show: true, type: 'delivered' })}
          active={statModal.type === 'delivered'}
        />
        <StatCard 
          label="Active ROs" 
          value={activeROs.length} 
          subtext="in progress" 
          onClick={() => { setActiveTab('workorders'); setRoFilter('active'); setStatModal({ show: false, type: null }); }}
        />
      </div>

      {/* ============ TABS ============ */}
      <div className="border-b border-neutral-200">
        <div className="flex gap-1 overflow-x-auto">
          {[
            { id: 'today', label: 'Today' },
            { id: 'precheck', label: 'Pre-Check', count: preCheckVehicles.length, color: 'red' },
            { id: 'inspections', label: 'Inspections', count: inspectionVehicles.length, color: 'blue' },
            { id: 'workorders', label: 'Work Orders' },
            { id: 'vendor', label: 'Vendor Needs', count: vendorNeeds.length, color: 'blue' },
            { id: 'arb', label: 'ARB Alerts', count: arbAlerts.length, color: 'orange' },
            { id: 'parts', label: 'Parts', count: partsWaitingDelivery.length, color: 'yellow' },
            { id: 'snoozed', label: 'Snoozed', count: snoozedROs.length, color: 'gray' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-neutral-600 hover:text-neutral-800 hover:border-neutral-300'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                  tab.color === 'orange' ? 'bg-orange-500 text-white' :
                  tab.color === 'red' ? 'bg-red-500 text-white' :
                  tab.color === 'blue' ? 'bg-blue-500 text-white' :
                  tab.color === 'yellow' ? 'bg-amber-500 text-white' :
                  'bg-neutral-400 text-white'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ============ TODAY TAB ============ */}
      {activeTab === 'today' && (
        <div className="space-y-3">
          {/* ============ DAILY WORKFLOW CHECKLIST ============ */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-blue-900">Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {currentUser?.name?.split(' ')[0] || 'Team'}!</h2>
                <p className="text-sm text-blue-700">Here's your day at a glance</p>
              </div>
              {!clockStatus.clockedIn ? (
                <button
                  onClick={async () => {
                    try {
                      await api.clockIn();
                      setClockStatus({ clockedIn: true, entry: { clockIn: new Date().toISOString() } });
                    } catch (err) {
                      alert('Failed to clock in: ' + err.message);
                    }
                  }}
                  className="px-4 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition"
                >
                  Clock In
                </button>
              ) : (
                <div className="text-right">
                  <div className="text-sm font-medium text-green-700 bg-green-100 px-3 py-1 rounded-full">Clocked In</div>
                  {clockStatus.entry?.clockIn && (
                    <div className="text-xs text-neutral-500 mt-1">
                      Since {new Date(clockStatus.entry.clockIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Workflow Steps */}
            <div className="space-y-2">
              {/* Step 1: Clock In */}
              <div className={`flex items-center justify-between p-3 rounded-lg ${clockStatus.clockedIn ? 'bg-green-50 border border-green-200' : 'bg-white border border-neutral-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${clockStatus.clockedIn ? 'bg-green-500 text-white' : 'bg-neutral-300 text-white'}`}>
                    {clockStatus.clockedIn ? '\u2713' : '1'}
                  </div>
                  <div>
                    <div className="font-medium text-sm">Clock In</div>
                    <div className="text-xs text-neutral-500">{clockStatus.clockedIn ? 'You\'re on the clock' : 'Start your day'}</div>
                  </div>
                </div>
                {clockStatus.clockedIn && <span className="text-green-600 text-xs font-medium">Done</span>}
              </div>
              
              {/* Step 2: Check Parts Room */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-white border border-neutral-200 hover:border-blue-300 cursor-pointer transition" onClick={() => setView('parts-room')}>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold bg-blue-500 text-white">2</div>
                  <div>
                    <div className="font-medium text-sm">Check Parts Room</div>
                    <div className="text-xs text-neutral-500">Parts arrived, orders to check</div>
                  </div>
                </div>
                <span className="text-blue-600 text-sm">Go to Parts Room →</span>
              </div>
              
              {/* Step 3: Pre-Check Vehicles */}
              <div className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition ${preCheckVehicles.length > 0 ? 'bg-red-50 border-red-200 hover:border-red-300' : 'bg-white border-neutral-200 hover:border-blue-300'}`} onClick={() => setActiveTab('precheck')}>
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${preCheckVehicles.length > 0 ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>
                    {preCheckVehicles.length === 0 ? '\u2713' : '3'}
                  </div>
                  <div>
                    <div className="font-medium text-sm">Pre-Check Vehicles</div>
                    <div className="text-xs text-neutral-500">Review flagged vehicles before arrival</div>
                  </div>
                </div>
                {preCheckVehicles.length > 0 ? (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">{preCheckVehicles.length} flagged</span>
                ) : (
                  <span className="text-green-600 text-xs font-medium">All clear</span>
                )}
              </div>
              
              {/* Step 4: Messages & Tasks */}
              <div className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition ${myTasks.length > 0 ? 'bg-amber-50 border-amber-200 hover:border-amber-300' : 'bg-white border-neutral-200 hover:border-blue-300'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${myTasks.length > 0 ? 'bg-amber-500 text-white' : 'bg-green-500 text-white'}`}>
                    {myTasks.length === 0 ? '\u2713' : '4'}
                  </div>
                  <div>
                    <div className="font-medium text-sm">Messages & Tasks</div>
                    <div className="text-xs text-neutral-500">Tasks assigned to you</div>
                  </div>
                </div>
                {myTasks.length > 0 ? (
                  <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full">{myTasks.length} pending</span>
                ) : (
                  <span className="text-green-600 text-xs font-medium">All done</span>
                )}
              </div>
              
              {/* Step 5: Inspections */}
              <div className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition ${inspectionVehicles.length > 0 ? 'bg-blue-50 border-blue-200 hover:border-blue-300' : 'bg-white border-neutral-200 hover:border-blue-300'}`} onClick={() => setActiveTab('inspections')}>
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${inspectionVehicles.length > 0 ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'}`}>
                    {inspectionVehicles.length === 0 ? '\u2713' : '5'}
                  </div>
                  <div>
                    <div className="font-medium text-sm">Inspections</div>
                    <div className="text-xs text-neutral-500">Delivered vehicles to inspect</div>
                  </div>
                </div>
                {inspectionVehicles.length > 0 ? (
                  <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">{inspectionVehicles.length} waiting</span>
                ) : (
                  <span className="text-green-600 text-xs font-medium">All done</span>
                )}
              </div>
              
              {/* Step 6: Work Orders */}
              <div className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition ${activeROs.length > 0 ? 'bg-indigo-50 border-indigo-200 hover:border-indigo-300' : 'bg-white border-neutral-200 hover:border-blue-300'}`} onClick={() => { setActiveTab('workorders'); setRoFilter('active'); }}>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold bg-indigo-500 text-white">6</div>
                  <div>
                    <div className="font-medium text-sm">Work Orders</div>
                    <div className="text-xs text-neutral-500">Continue repair work</div>
                  </div>
                </div>
                {activeROs.length > 0 ? (
                  <span className="bg-indigo-500 text-white text-xs font-bold px-2 py-1 rounded-full">{activeROs.length} active</span>
                ) : (
                  <span className="text-green-600 text-xs font-medium">None open</span>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={() => setView('parts-room')}
              className="px-4 py-2 bg-neutral-200 text-neutral-700 text-sm font-medium rounded-lg hover:bg-neutral-300"
            >
              Parts Room
            </button>
          </div>
        </div>
      )}

      {/* ============ VENDOR NEEDS TAB ============ */}
      {activeTab === 'vendor' && (
        <VendorNeedsBoard
          vendorNeeds={vendorNeeds}
          onVehicleClick={handleVendorVehicleClick}
        />
      )}

      {/* ============ ARB ALERTS TAB ============ */}
      {activeTab === 'arb' && (
        <div className="space-y-2">
          {arbAlerts.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">No ARB alerts</div>
          ) : arbAlerts.map(v => {
            const daysLeft = parseInt(v.daysUntilDeadline) || 0;
            return (
              <div key={v.id} className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-bold">{v.stockNumber}</span>
                  <span className="text-sm text-neutral-600">{v.year} {v.make} {v.model}</span>
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                    daysLeft <= 1 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {daysLeft === 0 ? 'TODAY' : `${daysLeft}d left`}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleArbAction(v.id, 'inspection_complete', 'No issues found')}
                    className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                  >
                    No Issues
                  </button>
                  <button 
                    onClick={() => {
                      const notes = window.prompt('Enter arbitration notes:');
                      if (notes) handleArbAction(v.id, 'arbitrate', notes);
                    }}
                    className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                  >
                    Arbitrate
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ============ PRE-CHECK TAB ============ */}
      {activeTab === 'precheck' && (
        <div className="space-y-2">
          {preCheckVehicles.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">No vehicles need pre-check</div>
          ) : preCheckVehicles.map(v => (
            <div key={v.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-bold">{v.stockNumber}</span>
                  <span className="text-sm text-neutral-600">{v.year} {v.make} {v.model}</span>
                </div>
                {v.whatItNeeds && (
                  <div className="text-xs text-neutral-500 mt-1">Notes: {v.whatItNeeds}</div>
                )}
              </div>
              <div className="flex gap-2">
                <VehicleFilesButton vehicleId={v.id} vehicle={v} className="text-xs" />
                <button
                  onClick={() => setPrecheckVehicle(v)}
                  className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                >
                  Begin
                </button>
                <button
                  onClick={() => setSnoozeModal({ show: true, vehicleId: v.id, stockNumber: v.stockNumber, type: 'precheck' })}
                  className="px-2 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600"
                >
                  Snooze
                </button>
                <button
                  onClick={() => handlePreCheckComplete(v)}
                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                >
                  Done
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ============ INSPECTIONS TAB ============ */}
      {activeTab === 'inspections' && (
        <div className="space-y-2">
          {inspectionVehicles.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">No vehicles ready for inspection</div>
          ) : inspectionVehicles.map(v => (
            <div 
              key={v.id} 
              onClick={() => handleVehicleClick(v)}
              className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-bold">{v.stockNumber}</span>
                <span className="text-sm text-neutral-600">{v.year} {v.make} {v.model}</span>
                {v.roNumber && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                    RO: {v.roNumber}
                  </span>
                )}
              </div>
              <span className="text-neutral-400">→</span>
            </div>
          ))}
        </div>
      )}

      {/* ============ PARTS TAB ============ */}
      {activeTab === 'parts' && (
        <div className="space-y-2">
          {partsWaitingDelivery.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">No parts waiting 5+ days</div>
          ) : partsWaitingDelivery.map(v => (
            <div 
              key={v.id}
              onClick={() => {
                setSelectedROId(v.roId);
                setView('ro-detail');
              }}
              className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-bold">{v.stockNumber}</span>
                <span className="text-sm text-neutral-600">{v.year} {v.make} {v.model}</span>
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                  {Math.round(v.daysInactive || 0)}d waiting
                </span>
              </div>
              <span className="text-neutral-400">→</span>
            </div>
          ))}
        </div>
      )}

      {/* ============ SNOOZED TAB ============ */}
      {activeTab === 'snoozed' && (
        <div className="space-y-2">
          {snoozedROs.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">No snoozed items</div>
          ) : snoozedROs.map(v => (
            <div 
              key={v.id}
              onClick={() => handleVehicleClick(v)}
              className="flex items-center justify-between p-3 bg-neutral-50 border border-neutral-200 rounded-lg hover:bg-neutral-100 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-bold">{v.stockNumber}</span>
                <span className="text-sm text-neutral-600">{v.year} {v.make} {v.model}</span>
                {v.snoozedUntil && (
                  <span className="text-xs bg-neutral-100 text-neutral-700 px-2 py-0.5 rounded">
                    Until {new Date(v.snoozedUntil).toLocaleDateString()}
                  </span>
                )}
              </div>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (window.confirm(`Wake up RO #${v.roNumber}?`)) {
                    try {
                      await api.updateRepairOrder(v.roId, { snoozedUntil: null, snoozeReason: null });
                      loadDashboard();
                    } catch (err) {
                      alert('Failed: ' + err.message);
                    }
                  }
                }}
                className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
              >
                Wake Up
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ============ WORK ORDERS TAB ============ */}
      {activeTab === 'workorders' && (
        <div className="space-y-4">
          {/* Filter Bar */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex gap-2">
              {[
                { id: 'active', label: 'Active', count: activeROs.length },
                { id: 'snoozed', label: 'Snoozed', count: snoozedROs.length },
                { id: 'completed', label: 'Completed', count: completedROs.length },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setRoFilter(f.id)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition ${
                    roFilter === f.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {f.label} ({f.count})
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Search stock, vehicle, RO..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="px-3 py-1.5 text-sm border border-neutral-200 rounded-lg w-64"
            />
          </div>

          {/* RO Table */}
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b bg-neutral-50">
                  <th className="px-3 py-2.5 font-medium">Stock #</th>
                  <th className="px-3 py-2.5 font-medium">Vehicle</th>
                  <th className="px-3 py-2.5 font-medium">VIN</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-3 py-2.5 font-medium">RO #</th>
                  <th className="px-3 py-2.5 font-medium">Days</th>
                  <th className="px-3 py-2.5 font-medium w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredROs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-neutral-400">
                      No work orders found
                    </td>
                  </tr>
                ) : (
                  filteredROs.map(v => {
                    const daysInRecon = v.createdAt 
                      ? Math.floor((Date.now() - new Date(v.createdAt).getTime()) / (1000 * 60 * 60 * 24)) 
                      : 0;
                    const isStuck = daysInRecon >= 5;
                    
                    return (
                      <tr
                        key={v.id}
                        onClick={() => handleVehicleClick(v)}
                        className={`border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer ${
                          isStuck ? 'bg-red-50' : ''
                        }`}
                      >
                        <td className="px-3 py-2.5 font-mono text-xs font-medium">{v.stockNumber}</td>
                        <td className="px-3 py-2.5">
                          <div className="font-medium">{v.year} {v.make} {v.model}</div>
                        </td>
                        <td className="px-3 py-2.5">
                          <VinDisplay vin={v.vin} />
                        </td>
                        <td className="px-3 py-2.5">
                          <StatusBadge status={v.roStatus} />
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs">{v.roNumber || '-'}</td>
                        <td className="px-3 py-2.5">
                          <span className={`font-mono text-xs ${isStuck ? 'text-red-600 font-bold' : ''}`}>
                            {daysInRecon}d
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex gap-1">
                            {v.roStatus !== 'complete' && v.roStatus !== 'closed' && (
                              <>
                                <button
                                  onClick={(e) => handleMarkComplete(e, v)}
                                  className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                >
                                  Done
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSnoozeModal({ show: true, roId: v.roId, roNumber: v.roNumber, stockNumber: v.stockNumber, type: 'ro' });
                                  }}
                                  className="px-2 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600"
                                >
                                  Snooze
                                </button>
                              </>
                            )}
                            {isAdmin && v.roId && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteRO(v.roId, v.roNumber, v.stockNumber);
                                }}
                                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ============ NEW RO MODAL ============ */}
      {showNewROForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">Create New Repair Order</h3>
            
            {/* Vehicle Type Tabs */}
            <div className="flex gap-2 mb-4">
              {[
                { id: 'inventory', label: 'Inventory' },
                { id: 'shop', label: 'Shop Vehicle' },
                { id: 'outside', label: 'Outside Service' },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => { setNewROType(t.id); setSelectedVehicleForRO(null); setServiceHistory([]); }}
                  className={`px-4 py-2 text-sm rounded-lg font-medium ${
                    newROType === t.id 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Inventory Selection - Searchable */}
            {newROType === 'inventory' && (
              <div className="relative">
                <label className="block text-sm font-medium mb-2">Search Vehicle</label>
                <input
                  type="text"
                  value={vehicleSearch}
                  onChange={e => {
                    setVehicleSearch(e.target.value);
                    setShowVehicleDropdown(true);
                    if (selectedVehicleForRO) {
                      const selectedText = `${selectedVehicleForRO.stockNumber} - ${selectedVehicleForRO.year} ${selectedVehicleForRO.make} ${selectedVehicleForRO.model}`;
                      if (e.target.value !== selectedText) {
                        setSelectedVehicleForRO(null);
                      }
                    }
                  }}
                  onFocus={() => setShowVehicleDropdown(true)}
                  placeholder="Type stock #, year, make, model, or VIN..."
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
                {selectedVehicleForRO && (
                  <span className="absolute right-3 top-9 text-green-600">✓</span>
                )}
                {showVehicleDropdown && (
                  <>
                    <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {(() => {
                        const search = vehicleSearch.toLowerCase();
                        const filtered = allInventoryVehicles.filter(v => 
                          !search ||
                          (v.stockNumber || '').toLowerCase().includes(search) ||
                          (v.year || '').toString().includes(search) ||
                          (v.make || '').toLowerCase().includes(search) ||
                          (v.model || '').toLowerCase().includes(search) ||
                          (v.vin || '').toLowerCase().includes(search)
                        );
                        if (filtered.length === 0) {
                          return <div className="p-3 text-sm text-neutral-500">No vehicles found</div>;
                        }
                        return (
                          <>
                            {filtered.slice(0, 20).map(v => (
                              <button
                                key={v.id}
                                type="button"
                                onClick={() => {
                                  handleSelectVehicleForRO(v);
                                  setVehicleSearch(`${v.stockNumber} - ${v.year} ${v.make} ${v.model}`);
                                  setShowVehicleDropdown(false);
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-100 border-b border-neutral-100 last:border-0"
                              >
                                <div className="font-medium">{v.stockNumber} - {v.year} {v.make} {v.model}</div>
                                {v.vin && <div className="text-xs"><VinDisplay vin={v.vin} /></div>}
                                {v.whatItNeeds && <div className="text-xs text-amber-600 truncate">Needs: {v.whatItNeeds}</div>}
                              </button>
                            ))}
                            {filtered.length > 20 && (
                              <div className="p-2 text-xs text-neutral-500 text-center bg-neutral-50">
                                Showing 20 of {filtered.length} - type more to narrow
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="fixed inset-0 z-40" onClick={() => setShowVehicleDropdown(false)} />
                  </>
                )}
                {selectedVehicleForRO && (
                  <div className="bg-blue-50 rounded-lg p-3 mt-3 mb-3">
                    <div className="font-medium">{selectedVehicleForRO.year} {selectedVehicleForRO.make} {selectedVehicleForRO.model}</div>
                    <div className="text-sm text-neutral-600">Stock: {selectedVehicleForRO.stockNumber}</div>
                  </div>
                )}
              </div>
            )}

            {/* Shop Vehicle Selection */}
            {newROType === 'shop' && (
              <div>
                <label className="block text-sm font-medium mb-2">Select Shop Vehicle</label>
                <select
                  value={selectedVehicleForRO?.id || ''}
                  onChange={(e) => {
                    const v = shopVehicles.find(v => v.id === parseInt(e.target.value));
                    handleSelectVehicleForRO(v);
                  }}
                  className="w-full px-3 py-2 border rounded-lg text-sm mb-3"
                >
                  <option value="">-- Select --</option>
                  {shopVehicles.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.stockNumber} - {v.year} {v.make} {v.model}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Outside Service Form */}
            {newROType === 'outside' && (
              <form onSubmit={handleAddOutsideService}>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input type="text" placeholder="Customer Name *" value={customerForm.customerName} onChange={e => setCustomerForm({...customerForm, customerName: e.target.value})} className="px-2 py-1.5 text-sm border rounded" required />
                  <input type="text" placeholder="Phone" value={customerForm.customerPhone} onChange={e => setCustomerForm({...customerForm, customerPhone: e.target.value})} className="px-2 py-1.5 text-sm border rounded" />
                </div>
                <div className="flex gap-2 mb-3">
                  <input type="text" placeholder="VIN" value={customerForm.vin} onChange={e => setCustomerForm({...customerForm, vin: e.target.value.toUpperCase()})} className="flex-1 px-2 py-1.5 text-sm border rounded font-mono" maxLength={17} />
                  <button type="button" onClick={() => decodeCustomerVin(customerForm.vin)} disabled={customerDecoding || customerForm.vin.length !== 17} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                    {customerDecoding ? 'Decoding...' : 'Decode'}
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <input type="text" placeholder="Year *" value={customerForm.year} onChange={e => setCustomerForm({...customerForm, year: e.target.value})} className="px-2 py-1.5 text-sm border rounded" required />
                  <input type="text" placeholder="Make *" value={customerForm.make} onChange={e => setCustomerForm({...customerForm, make: e.target.value})} className="px-2 py-1.5 text-sm border rounded" required />
                  <input type="text" placeholder="Model *" value={customerForm.model} onChange={e => setCustomerForm({...customerForm, model: e.target.value})} className="px-2 py-1.5 text-sm border rounded" required />
                  <input type="text" placeholder="Color" value={customerForm.color} onChange={e => setCustomerForm({...customerForm, color: e.target.value})} className="px-2 py-1.5 text-sm border rounded" />
                </div>
                <textarea placeholder="Work Needed" value={customerForm.customerComplaint} onChange={e => setCustomerForm({...customerForm, customerComplaint: e.target.value})} className="w-full px-2 py-1.5 text-sm border rounded mb-3" rows={2} />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setShowNewROForm(false)} className="px-3 py-1.5 text-sm text-neutral-600">Cancel</button>
                  <button type="submit" className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">Create</button>
                </div>
              </form>
            )}

            {/* Service History */}
            {serviceHistory.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <h4 className="text-sm font-medium text-neutral-700 mb-2">Service History ({serviceHistory.length})</h4>
                <div className="max-h-32 overflow-y-auto space-y-2">
                  {serviceHistory.map(ro => (
                    <div key={ro.id} className="bg-neutral-50 rounded p-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">{ro.roNumber}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${ro.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{ro.status}</span>
                      </div>
                      <div className="text-xs text-neutral-500">{new Date(ro.createdAt).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Create Button for Inventory/Shop */}
            {(newROType === 'inventory' || newROType === 'shop') && (
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setShowNewROForm(false)} className="px-3 py-1.5 text-sm text-neutral-600">Cancel</button>
                <button
                  onClick={handleCreateROFromSelection}
                  disabled={!selectedVehicleForRO}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Create RO
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ PRECHECK MODAL ============ */}
      {precheckVehicle && (
        <PrecheckModal
          vehicle={precheckVehicle}
          users={users}
          vendors={allVendors}
          onClose={() => setPrecheckVehicle(null)}
          onComplete={() => {
            setPrecheckVehicle(null);
            loadDashboard();
            if (onRefresh) onRefresh();
          }}
          onCreateRO={handleCreateROFromPrecheck}
        />
      )}

      {/* ============ STAT MODAL - Inbound/Delivered Vehicle Lists ============ */}
      {statModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">
                {statModal.type === 'inbound' ? `Inbound Vehicles (${inboundCount})` : `Delivered Last 7 Days (${deliveredCount})`}
              </h3>
              <button
                onClick={() => setStatModal({ show: false, type: null })}
                className="text-neutral-400 hover:text-neutral-600 text-xl"
              >
                x
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-xs text-neutral-500 border-b">
                    <th className="px-2 py-2 font-medium">Stock #</th>
                    <th className="px-2 py-2 font-medium">Vehicle</th>
                    <th className="px-2 py-2 font-medium">Status</th>
                    <th className="px-2 py-2 font-medium">Days</th>
                  </tr>
                </thead>
                <tbody>
                  {(statModal.type === 'inbound' ? inboundVehicles : deliveredVehicles).map(v => {
                    const days = v.purchaseDate 
                      ? Math.floor((Date.now() - new Date(v.purchaseDate).getTime()) / (1000 * 60 * 60 * 24))
                      : '-';
                    return (
                      <tr key={v.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="px-2 py-2 font-mono text-xs font-medium">{v.stockNumber}</td>
                        <td className="px-2 py-2">{v.year} {v.make} {v.model}</td>
                        <td className="px-2 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            v.transportStatus === 'delivered' ? 'bg-green-100 text-green-700' :
                            v.transportStatus === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                            v.transportStatus === 'in_transit' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {v.transportStatus || 'pending'}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-xs">{days}d</td>
                      </tr>
                    );
                  })}
                  {(statModal.type === 'inbound' ? inboundVehicles : deliveredVehicles).length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-2 py-8 text-center text-neutral-400">
                        No vehicles found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-4 pt-4 border-t flex justify-end">
              <button
                onClick={() => setStatModal({ show: false, type: null })}
                className="px-4 py-2 bg-neutral-200 text-neutral-700 rounded-lg hover:bg-neutral-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ SNOOZE MODAL ============ */}
      {snoozeModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">
              Snooze {snoozeModal.type === 'precheck' ? snoozeModal.stockNumber : `RO #${snoozeModal.roNumber}`}
            </h3>
            
            {snoozeModal.type === 'precheck' && (
              <div className="mb-4">
                <button
                  onClick={async () => {
                    try {
                      await api.updateVehicle(snoozeModal.vehicleId, { precheckSnoozedUntilDelivered: true });
                      setSnoozeModal({ show: false });
                      loadDashboard();
                      if (onRefresh) onRefresh();
                    } catch (err) {
                      alert('Failed: ' + err.message);
                    }
                  }}
                  className="w-full px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-medium mb-3"
                >
                  Snooze Until Delivered
                </button>
                <div className="text-center text-sm text-neutral-500 mb-3">or</div>
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Snooze for:</label>
              <div className="flex gap-2 flex-wrap">
                {[1, 2, 3, 5, 7, 14].map(d => (
                  <button
                    key={d}
                    onClick={() => setSnoozeDays(d)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      snoozeDays === d ? 'bg-blue-600 text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }`}
                  >
                    {d} days
                  </button>
                ))}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Reason (optional)</label>
              <input
                type="text"
                value={snoozeReason}
                onChange={e => setSnoozeReason(e.target.value)}
                placeholder="e.g., Waiting for parts..."
                className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setSnoozeModal({ show: false }); setSnoozeDays(3); setSnoozeReason(''); }}
                className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={snoozeModal.type === 'precheck' ? handlePrecheckSnooze : handleSnooze}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Snooze
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReconDashboard;
